import { useEffect, useMemo, useState } from 'react';
import {
  EVENT_TYPES,
  SCENARIO_TAGS,
  formatEventType,
  formatScenarioTag,
  makeEventMarker,
  type EventMarker,
  type EventType,
  type ScenarioTag
} from '../../lib/decode/eventMarkers';
import { EventTimeline } from './EventTimeline';

type EventMarkerPanelProps = {
  captureStartMs: number | null;
  captureEndMs: number | null;
  markers: EventMarker[];
  selectedEventId: string | null;
  scenarioTag: ScenarioTag;
  correlationBusy: boolean;
  onAddMarker: (marker: EventMarker) => void;
  onRemoveMarker: (eventId: string) => void;
  onSelectEvent: (eventId: string) => void;
  onScenarioTagChange: (tag: ScenarioTag) => void;
  onRunCorrelation: () => void;
  runButtonHighlighted?: boolean;
  suggestedEvent?: {
    eventType: EventType;
    label: string;
    notes: string;
    requestId: number;
  } | null;
};

const QUICK_EVENTS: Array<{ type: EventType; label: string }> = [
  { type: 'brake', label: 'Brake press' },
  { type: 'throttle', label: 'Throttle input' },
  { type: 'steering', label: 'Steering movement' },
  { type: 'idle', label: 'Idle' },
  { type: 'custom', label: 'Custom' }
];

function formatMs(value: number) {
  return `${value.toFixed(0)} ms`;
}

function midpoint(startMs: number | null, endMs: number | null) {
  if (startMs === null || endMs === null) return 0;
  return Math.max(0, startMs + (endMs - startMs) / 2);
}

export function EventMarkerPanel({
  captureStartMs,
  captureEndMs,
  markers,
  selectedEventId,
  scenarioTag,
  correlationBusy,
  onAddMarker,
  onRemoveMarker,
  onSelectEvent,
  onScenarioTagChange,
  onRunCorrelation,
  runButtonHighlighted = false,
  suggestedEvent
}: EventMarkerPanelProps) {
  const helperTimestamp = useMemo(() => midpoint(captureStartMs, captureEndMs), [captureEndMs, captureStartMs]);
  const [timestampMs, setTimestampMs] = useState(helperTimestamp);
  const [eventType, setEventType] = useState<EventType>('brake');
  const [label, setLabel] = useState('Brake press');
  const [durationMs, setDurationMs] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    setTimestampMs(helperTimestamp);
  }, [helperTimestamp]);

  useEffect(() => {
    if (!suggestedEvent) return;
    setEventType(suggestedEvent.eventType);
    setLabel(suggestedEvent.label);
    setNotes(suggestedEvent.notes);
  }, [suggestedEvent]);

  function addMarker(type = eventType, nextLabel = label) {
    if (!Number.isFinite(timestampMs) || timestampMs < 0) return;
    const marker = makeEventMarker({
      timestampMs,
      eventType: type,
      label: nextLabel,
      notes: notes.trim() || undefined,
      durationMs: durationMs.trim() ? Math.max(0, Number.parseFloat(durationMs)) : undefined
    });
    onAddMarker(marker);
  }

  function addQuickEvent(type: EventType, nextLabel: string) {
    setEventType(type);
    setLabel(nextLabel);
    addMarker(type, nextLabel);
  }

  return (
    <div className="event-marker-panel">
      <p className="event-marker-step eyebrow">Step 2: Add an event marker</p>
      <div className="event-scenario-row">
        <label>
          Scenario tag
          <select value={scenarioTag} onChange={(event) => onScenarioTagChange(event.target.value as ScenarioTag)}>
            {SCENARIO_TAGS.map((tag) => <option key={tag} value={tag}>{formatScenarioTag(tag)}</option>)}
          </select>
        </label>
        <div className="event-helper-copy">
          <strong>Correlation is a discovery aid, not proof.</strong>
          <span>Use repeated scenarios to validate a signal.</span>
        </div>
      </div>

      <EventTimeline
        startMs={captureStartMs}
        endMs={captureEndMs}
        markers={markers}
        selectedEventId={selectedEventId}
        onSelectEvent={onSelectEvent}
      />

      <p className="event-quick-grid-label eyebrow">Event type shortcuts</p>
      <div className="event-quick-grid">
        {QUICK_EVENTS.map((item) => (
          <button className="button secondary" key={item.type} type="button" onClick={() => addQuickEvent(item.type, item.label)}>
            {item.label}
          </button>
        ))}
      </div>

      <div className="event-form-grid">
        <label title="timestamp_ms">
          Time
          <input type="number" min={0} step="1" value={Number.isFinite(timestampMs) ? timestampMs : 0} onChange={(event) => setTimestampMs(Number.parseFloat(event.target.value) || 0)} />
          <small className="field-hint">Pre-filled from capture midpoint &mdash; adjust if needed.</small>
        </label>
        <label title="event_type">
          Event type
          <select value={eventType} onChange={(event) => setEventType(event.target.value as EventType)}>
            {EVENT_TYPES.map((type) => <option key={type} value={type}>{formatEventType(type)}</option>)}
          </select>
        </label>
        <label title="label">
          Label
          <input value={label} onChange={(event) => setLabel(event.target.value)} placeholder="Brake press near stop sign" />
        </label>
        <label title="duration_ms">
          Duration
          <input value={durationMs} onChange={(event) => setDurationMs(event.target.value)} inputMode="decimal" placeholder="optional" />
        </label>
        <label className="event-notes-field" title="notes">
          Notes
          <textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="What happened at this point in the drive?" />
        </label>
      </div>

      <div className="event-actions">
        <button className="button secondary" type="button" onClick={() => addMarker()}>Add event marker</button>
        <button className={runButtonHighlighted ? 'button primary pulse-highlight' : 'button primary'} type="button" disabled={!selectedEventId || correlationBusy} onClick={onRunCorrelation}>
          {correlationBusy ? 'Running...' : 'Run correlation'}
        </button>
      </div>

      <div className="event-marker-list">
        {markers.length === 0 ? (
          <p className="panel-note">Add an event marker to run correlation. If no cursor exists, use the midpoint helper timestamp and adjust it manually.</p>
        ) : markers.map((marker) => (
          <article className={marker.id === selectedEventId ? 'event-marker-card active' : 'event-marker-card'} key={marker.id}>
            <button type="button" onClick={() => onSelectEvent(marker.id)}>
              <strong>{marker.label}</strong>
              <span>{formatEventType(marker.eventType)} / {formatMs(marker.timestampMs)}</span>
            </button>
            <button className="event-remove-button" type="button" onClick={() => onRemoveMarker(marker.id)} aria-label={`Remove ${marker.label}`}>Remove</button>
          </article>
        ))}
      </div>
    </div>
  );
}
